import { createFileRoute } from "@tanstack/react-router";
import type { ClientLocationPlanBaseData } from "api-types/locationPlans/locationPlansResponses";
import type { ClientLotCommunityPlanHydrated } from "api-types/lotCommunityPlans/lotCommunityPlansResponses";
import type { ClientLotHydrated } from "api-types/lots/lotsResponses";
import { assertIsNotNullOrUndefined, assertIsNotUndefined } from "common/utils/assertOfType";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import getLotNumberDisplay from "lib/components/builders/communities/plans/getLotNumberDisplay";
import { canViewChildLocations } from "lib/components/builders/communities/utils/locationUtils";
import { useLocationDescendantLotCount } from "lib/components/builders/communities/utils/useLocationDescendantLotCount";
import PlanLibraryHeaderActions from "lib/components/builders/plan-library/header/PlanLibraryHeaderActions";
import PlanLibrarySubheader from "lib/components/builders/plan-library/header/PlanLibrarySubheader";
import PlanLibrary from "lib/components/builders/plan-library/PlanLibrary";
import {
  useFetchLotCommunityPlan,
  useFetchPlanLibraryLot,
  useFetchCommunityPlanBaseData,
} from "lib/components/builders/plan-library/planLibraryUtils";
import PlanLibraryElevationsTab from "lib/components/builders/plan-library/tab/PlanLibraryElevationsTab";
import PlanLibraryHomePlansTab from "lib/components/builders/plan-library/tab/PlanLibraryHomePlansTab";
import PlanLibraryOptionSetsTab from "lib/components/builders/plan-library/tab/PlanLibraryOptionSetsTab";
import {
  builderPlanLibraryPath,
  locationDefaultPath,
  locationLotsPath,
  locationPlanLibraryPath,
  masterPlanLibraryPath,
  useAppRouterParams,
} from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import Loading from "ui/components/Loading";
import useTabbedMenu from "ui/hooks/useTabbedMenu";
import withAuthentication from "../../../../../../../../../components/withAuthentication";

function PlanLibraryLotCommunityPlan() {
  const { builderId, locationId, locationPlanId, lotId } = useAppRouterParams();
  const { builderLoading, builder, hasPermission } = useUserContext();
  const descendantsHaveLots = useLocationDescendantLotCount({ builderId, locationId });

  const lot: ClientLotHydrated | null = useFetchPlanLibraryLot(builderId ?? "", locationId ?? "", lotId ?? "");
  const lotCommunityPlan: ClientLotCommunityPlanHydrated | null = useFetchLotCommunityPlan({
    builderId: builderId ?? "",
    locationId: locationId ?? "",
    lotId: lotId ?? "",
    communityPlanId: locationPlanId ?? "",
  });
  const locationPlanBaseData: ClientLocationPlanBaseData | null = useFetchCommunityPlanBaseData({
    builderId: builderId ?? "",
    locationId: locationId ?? "",
    locationPlanId: locationPlanId ?? "",
  });

  const allowHomePlans = locationPlanBaseData?.location?.allowHomePlans ?? true;
  const tabs = ["option_sets", "elevations", ...(allowHomePlans ? ["home_plans"] : [])];
  const { tab, setTab, menuItems } = useTabbedMenu(tabs);
  // Prevent empty pane if user manually navigates to a hidden tab
  if (!tabs.includes(tab)) {
    setTab(tabs[0]);
  }

  if (!builderLoading && locationPlanBaseData && lotCommunityPlan && lot) {
    assertIsNotUndefined(builder);
    assertIsNotUndefined(builderId);
    assertIsNotUndefined(locationId);
    assertIsNotUndefined(locationPlanId);
    assertIsNotNullOrUndefined(locationPlanBaseData.masterPlan.name);
    assertIsNotUndefined(locationPlanBaseData.location?.name);
    return (
      <PlanLibrary
        planId={lotCommunityPlan.id}
        planType="lot"
        builder={builder}
        locationPlanBaseData={locationPlanBaseData}
        lotCommunityPlan={lotCommunityPlan}
        lot={lot}
        isReadOnly={!hasPermission("EDIT_LOT_COMMUNITY_PLANS")}
      >
        <BuilderPortalLayout
          pageTitle={locationPlanBaseData.locationPlan.name}
          breadcrumbs={[
            { text: "Master Plans", href: builderPlanLibraryPath(builderId) },
            {
              text: locationPlanBaseData.masterPlan.name,
              href: masterPlanLibraryPath(builderId, locationPlanBaseData.masterPlan.id, tab),
            },
            {
              text: locationPlanBaseData.location.name,
              href: locationDefaultPath(
                builderId,
                locationPlanBaseData.location.id,
                locationPlanBaseData.location.locationLevel?.type,
                locationPlanBaseData.location.disableLots,
                descendantsHaveLots,
                hasPermission("VIEW_LOTS"),
                canViewChildLocations(
                  locationPlanBaseData.location.locationLevel?.type,
                  builder?.locationSettings,
                  hasPermission("VIEW_CHILD_LOCATIONS"),
                ),
              ),
            },
            {
              text: locationPlanBaseData.locationPlan.name,
              href: locationPlanLibraryPath({ builderId, locationId, locationPlanId, tab }),
            },
            { text: "Lots", href: locationLotsPath(builderId, locationId) },
            { text: `Lot ${lot.lotNumber}`, href: "#" },
          ]}
          header={`${locationPlanBaseData.locationPlan.name} on Lot ${getLotNumberDisplay(lot)}`}
          subheaderItems={[<PlanLibrarySubheader key="plan-library-subheader" />]}
          headerActions={<PlanLibraryHeaderActions />}
          menuItems={menuItems}
          selected="plans"
          wide
        >
          <div className="builder-portal-layout-content">
            {tab === "option_sets" && <PlanLibraryOptionSetsTab />}
            {tab === "elevations" && <PlanLibraryElevationsTab />}
            {tab === "home_plans" && allowHomePlans && <PlanLibraryHomePlansTab planType="lot" />}
          </div>
        </BuilderPortalLayout>
      </PlanLibrary>
    );
  }

  return <Loading />;
}

export const Route = createFileRoute(
  "/builders/$builder_id/locations/$location_id/location-plans/$location_plan_id/lots/$lot_id/",
)({
  component: withAuthentication(PlanLibraryLotCommunityPlan, {
    permissions: ["VIEW_LOT_COMMUNITY_PLANS"],
    resource: {
      resourceType: "community_plans",
      routeParamName: "location_plan_id",
    },
  }),
});
